import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveRepresentative } from '../../../services/representative/representativeService';
import Customer from '../../../models/Customer';
import styles from './RepresentativeView.module.css';
import commonStyles from '../common.module.css';
import Input from '../../common/Input/Input';
import APIError from '../../../services/APIError';
import Error from '../../common/Error/Error';
import warningLogo from '../../../assets/images/warning.png';
import { getCountryByValue } from '../../common/CountrySelect/countries';

type RepresentativeViewProps = {
  customer: Customer,
  representative: Customer,
  cancelRepresentative: (representative: Customer) => void
}

function RepresentativeView(props: RepresentativeViewProps) {
  const { t } = useTranslation();
  const navigation = useNavigate();
  const [relationDescription, setRelationDescription] = useState<string>();
  const [error, setError] = useState(false);
  const [errorText, setErrorText] = useState('');

  const handleAdd = async () => {
    if (relationDescription) {
      try {
        await saveRepresentative({
          customer: props.customer,
          representative: props.representative,
          relationDescription,
        });
        navigation('/customer');
      } catch (err) {
        if (err instanceof APIError) {
          console.log(`Failed to save representative: ${err.errorBody}`);
          setErrorText(t(`customer.validation.${err.errorBody}`));
        } else {
          console.log(err);
          setErrorText(t(`customer.validation.relationAlreadyExists`));
        }
      }
    } else {
      setError(true);
    }
  };

  const handleCancel = () => {
    props.cancelRepresentative(props.representative);
  };

  return (
    <>
      <div>
        {errorText &&
          <Error
            messageText={errorText}
            logoSrc={warningLogo}
            logoAltText={t('customer.validation.warningLogo')}
          />
        }
      </div>
      <div>
        <div className={commonStyles.row}>
          <div className={styles.field}>
            <div className={styles.label}>
              {t('customer.dashboard.basicInformation.label.name')}
            </div>
            <div>
              {props.representative.firstName}
              {' '}
              {props.representative.name}
            </div>
          </div>
          <div className={styles.field}>
            <div className={styles.label}>
              {t('customer.dashboard.basicInformation.label.personalId')}
            </div>
            <div>{props.representative.registrationCode}</div>
          </div>
          <div className={styles.field}>
            <div className={styles.label}>
              {t('customer.dashboard.basicInformation.label.citizenship')}
            </div>
            <div>{getCountryByValue(props.representative.country)}</div>
          </div>
          <div className={styles.field}>
            <div className={styles.label}>
              {t('customer.dashboard.basicInformation.label.language')}
            </div>
            <div>{props.representative.preferredLanguage}</div>
          </div>
        </div>
        <div className={commonStyles.row}>
          <div className={styles.field}>
            <div className={styles.label}>
              {t('customer.dashboard.basicInformation.label.email')}
            </div>
            <div>{props.representative.email}</div>
          </div>
          <div className={styles.field}>
            <div className={styles.label}>
              {t('customer.dashboard.basicInformation.label.phone')}
            </div>
            <div>{props.representative.phone}</div>
          </div>
          <div className={styles.field} style={{ flexGrow: 2 }}>
            <Input
              id='role'
              label={t('representative.view.label.role')}
              placeholder={t('representative.view.placeholder.role')}
              maxLength={100}
              required
              type='text'
              onUpdate={(value) => setRelationDescription(value)}
              error={error && !relationDescription}
            />
          </div>
        </div>
      </div>
      <div className={styles.buttonsContainer}>
        <div
          role='button'
          tabIndex={0}
          onClick={handleAdd}
          onKeyDown={handleAdd}
          className={styles.button}
        >
          {t('common.add')}
        </div>
        <div
          role='button'
          tabIndex={0}
          onClick={handleCancel}
          onKeyDown={handleCancel}
          className={styles.button}
        >
          {t('common.cancel')}
        </div>
      </div>
    </>
  );
}

export default RepresentativeView;
